import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import SankeyUtil from './SankeyUtil';
import { isEmpty, clone, lowerCase, unset } from 'lodash';
import { buildGraphData } from './DataUtil';
import bothLogos from '../../../assets/img/UNDP BIOFIN logo.png';
import refreshIcon from '../../../assets/img/refresh-time.svg';
import Filters from './Filters';
import { ALL_OPTIONS_COLUMN_NAME, CANVAS_HEIGHT, CANVAS_WIDTH } from '../../../Constants';
import('./Graph.scss');
const d3 = window.d3;

const Graph = forwardRef(({graphRecord, graphHeight, nodeDrag=false, iconDrag=false, nodeTextAnchorChangeEnabled=false, highlightMode}, ref) => {

    const CANVAS_CONTAINER_ID = 'canvas-container';

    const graphRef = useRef(null);
    const graphHeader = useRef(null);
    const canvasContainerRef = useRef(null);
    const sankeyUtil = useRef(null);
    const previousGraphRecord = useRef({});

    const isGraphReady = useRef(false);

    const [currentFilter, setCurrentFilter] = useState({});

    useEffect(()=>{
        if(isEmpty(graphRecord)){
            clearGraph();
            previousGraphRecord.current = {};
            return;
        }
        
        // avoid render twice
        if(JSON.stringify(graphRecord) !== JSON.stringify(previousGraphRecord.current)){
            previousGraphRecord.current = clone(graphRecord);
            reDrawGraph(graphRecord);
        }

    }, [graphRecord]);

    useEffect(()=>{
        if(isGraphReady.current){
            onRestore();
        }
    },[highlightMode]);

    const reDrawGraph = async (graphRecord) => { 

        if(!graphRecord.linksFile){
            return;
        }
        
        const canvasWidth = CANVAS_WIDTH;
        const canvasHeight = CANVAS_HEIGHT;


        // set the dimensions and margins of the graph
        var padding = { top: 10, right: 10, bottom: 10, left: 10 },
            workWidth = (canvasWidth - padding.left - padding.right),
            workHeight = (canvasHeight - padding.top - padding.bottom);

        const CELL_HEIGHT = 40;

        // clear before render
        clearGraph();
        // append the svg object to the body of the page
        const svg = d3.select("#" + CANVAS_CONTAINER_ID)
            .append("svg")
            .attr("viewBox", `0 0 ${canvasWidth} ${canvasHeight}`)
            .attr("preserveAspectRatio", "xMinYMin meet"); ;

        // background object (needed for mouseout events)
        svg
            .append("rect")
            .attr("width", "100%")
            .attr("height", "100%")
            .style("fill", "transparent");

        
        svg.attr("width", '100%')
            //.attr("height", 'auto'); // do not set height to auto, throws error

        // Set the sankey diagram properties
        const sankey = d3.sankey()
            .nodeWidth(30)
            .nodePadding(8) // this is vertical padding between nodes before relocate
            .size([workWidth, workHeight]); 

        sankeyUtil.current = new SankeyUtil(d3, sankey, svg);

        const {graphData, categories} = await buildGraphData(graphRecord, workWidth, workHeight, padding, CELL_HEIGHT, sankey);

        // draw legends layer
        const legendsHeight = sankeyUtil.current.drawCategoriesLayer(graphRecord, workWidth, workHeight, padding, iconDrag);
        // raw graph using the remaining space
        sankeyUtil.current.drawGraph(graphData, workWidth, workHeight - legendsHeight, nodeDrag, nodeTextAnchorChangeEnabled, highlightMode, currentFilter);

        isGraphReady.current = true;
    };

    const clearGraph = function(){
        d3.select(`#${CANVAS_CONTAINER_ID}`).html('');
    }

    const getMetadata = () => {
        if(sankeyUtil.current){
            const nodesDetails = sankeyUtil.current.getNodesDetails();
            const labelsDetails = sankeyUtil.current.getLabelsDetails();
            return {
                nodesDetails,
                labelsDetails
            };
        }else{
            return undefined;
        }
    }

    // this is to expose methods to make them public
    useImperativeHandle(ref, () => ({
        getMetadata: getMetadata
    }));

    const onRestore = function(){
        // use the previous version as original
        const cleanRecord = clone(previousGraphRecord.current);
        reDrawGraph(cleanRecord);
        sankeyUtil.current.setMoreDetails('');
    }

    const onTypeSelected = function(event) {
        const type = event.target.id;
        let value = event.target.value;

        const newFilterObject = {
            ...currentFilter,
            [type]: value
        };

        // clean invalid values
        if(value === ALL_OPTIONS_COLUMN_NAME){
            unset(newFilterObject, [type]);
        }

        setCurrentFilter(newFilterObject);

        sankeyUtil.current.doFiltering(newFilterObject);
    }

    return (
        <div ref={graphRef} className='graph'>
            {
                !isEmpty(graphRecord) && <>
                    <div ref={graphHeader} className='graph-header'>
                        <div></div>
                        <div className='title-container'>
                            <h1 className='title'>{graphRecord.name}</h1>
                            {
                                currentFilter[Filters.NAMES.GRAPH_TYPE] &&  <h2 className='sub-title'>{lowerCase(currentFilter[Filters.NAMES.GRAPH_TYPE]) === 'c'? '': currentFilter[Filters.NAMES.GRAPH_TYPE]}</h2>
                            }
                        </div>
                        <div className='right-title-container'>
                            <div className='logos-container'>
                                <img className="both-logos" src={bothLogos} alt=""/>
                            </div>
                            <div className='controls-box'>
                                <div className="filters">
                                    { graphRecord.template.filters.map((filter, filterIndex) => (
                                        <div key={filterIndex} className='filter'>
                                            <label htmlFor={filter.type} className='filter-label'> {filter.name}: </label>
                                            <select id={filter.type} onChange={onTypeSelected} className='primary-dropdown'>
                                                { filter.options.map(option=>(
                                                    <option key={option.id} value={option.name}>{option.text}</option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className='restore-btn primary-button' onClick={onRestore}>
                                    <img src={refreshIcon} alt="Restaurar" title="Restaurar" className='refresh-icon'/>
                                </button>
                            </div>
                        </div>
                    </div>
                    
                </>
            } 
            <div ref={canvasContainerRef} id={CANVAS_CONTAINER_ID} className='canvas-container'></div>
            <div className='details-container'>
                <p className='more-details-title'>Definición/Comentario:</p>
                <div className='more-details-box'>
                    <div className='more-details-content' id="more-details-content"></div>
                </div>
            </div>
            <div className='disclaimer'>Powered by <a href='https://iamstudio.co/' target='_blank' rel="noreferrer">iamstudio.co</a></div>
        </div>
    );
});

export default Graph;
